import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { writeFetch } from '../../api';
import { useAuth } from '../../context/AuthContext';

const { FiX, FiCheck, FiZap, FiShield, FiStar, FiLoader, FiAlertCircle } = FiIcons;

const PLACEHOLDER_COVER = 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?q=80&w=2070&auto=format&fit=crop';
const PLACEHOLDER_PROFILE = 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1288&auto=format&fit=crop';

const CreateMemorialModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    birthDate: '',
    deathDate: '',
    bio: '',
    birthPlace: '',
    restingPlace: '',
  });

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePublish = async () => {
    if (!user) { setError('You must be signed in to create a memorial.'); return; }
    if (!formData.name.trim()) { setError('Please enter the full name.'); return; }

    setSaving(true);
    setError('');

    try {
      // ── Step 1: Create loved_one record ──────────────────────────────────
      const newLovedOneId = crypto.randomUUID();
      const lovedOneRes = await writeFetch('create/loved_ones', {
        method: 'POST',
        body: JSON.stringify({
          id: newLovedOneId,
          user_id: user.id,
          owner_id: user.id,
          first_name: formData.name.split(' ')[0] || formData.name,
          last_name: formData.name.split(' ').slice(1).join(' ') || '',
          date_of_birth: formData.birthDate || null,
          date_of_passing: formData.deathDate || null,
          biography: formData.bio || '',
          profile_image: PLACEHOLDER_PROFILE,
          cover_image: PLACEHOLDER_COVER,
          birth_place: formData.birthPlace || '',
          resting_place: formData.restingPlace || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }),
      });

      // NCB returns 201 on success; parse the response body
      let lovedOneData;
      try {
        lovedOneData = await lovedOneRes.json();
      } catch {
        throw new Error('Could not read server response for loved one. Please try again.');
      }

      // NCB success response shape: { status: "success", message: "...", id: <number> }
      if (!lovedOneRes.ok || lovedOneData.status !== 'success') {
        const msg = lovedOneData?.message || lovedOneData?.error || `Server error ${lovedOneRes.status}`;
        throw new Error(`Failed to create loved one record: ${msg}`);
      }

      // The new record's ID comes back as lovedOneData.id (integer) or we fall back to our random one
      const lovedOneId = lovedOneData.id || newLovedOneId;

      // ── Step 2: Create memorial_page record ───────────────────────────────
      const slug = `${formData.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;
      const newMemPageId = crypto.randomUUID();

      const memPageRes = await writeFetch('create/memorial_pages', {
        method: 'POST',
        body: JSON.stringify({
          id: newMemPageId,
          user_id: user.id,
          owner_id: user.id,
          loved_one_id: lovedOneId,
          title: `In Memory of ${formData.name}`,
          slug,
          description: formData.bio || '',
          theme: 'classic',
          privacy_setting: 'public',
          is_published: 1,
          visit_count: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }),
      });

      let memPageData;
      try {
        memPageData = await memPageRes.json();
      } catch {
        throw new Error('Could not read server response for memorial page. Please try again.');
      }

      if (!memPageRes.ok || memPageData.status !== 'success') {
        const msg = memPageData?.message || memPageData?.error || `Server error ${memPageRes.status}`;
        throw new Error(`Failed to create memorial page: ${msg}`);
      }

      // ── Success ───────────────────────────────────────────────────────────
      setStep(1);
      setFormData({ name: '', birthDate: '', deathDate: '', bio: '', birthPlace: '', restingPlace: '' });
      onClose();

      // Notify Explore page to refresh
      window.dispatchEvent(new CustomEvent('memorial-created', {
        detail: { lovedOneId, memorialPageId: memPageData.id || newMemPageId, name: formData.name }
      }));

    } catch (err) {
      console.error('[CreateMemorial]', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const canProceed = step === 1 ? formData.name.trim().length > 0 : true;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/70 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark flex flex-col max-h-[90vh]"
      >
        {/* Progress Header */}
        <div className="flex border-b border-brand-dark bg-brand-light/50 flex-shrink-0">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              onClick={() => s < step && setStep(s)}
              className={`flex-1 py-5 text-center text-[10px] font-black uppercase tracking-[0.2em] transition-all ${
                step === s
                  ? 'text-accent-gold border-b-2 border-accent-gold bg-white'
                  : s < step
                  ? 'text-green-600 cursor-pointer'
                  : 'text-text-muted opacity-40'
              }`}
            >
              {s < step ? '✓ ' : ''}Step {s}
            </div>
          ))}
        </div>

        <div className="p-8 md:p-12 overflow-y-auto flex-grow">
          <div className="flex justify-between items-start mb-10">
            <div>
              <div className="flex items-center gap-2 text-accent-gold mb-2">
                <SafeIcon icon={FiStar} className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-widest">New Sanctuary</span>
              </div>
              <h2 className="text-3xl font-serif text-accent-navy">Create a Legacy</h2>
              <p className="text-sm text-text-muted font-light mt-1">Preserve their story with dignity and love.</p>
            </div>
            <button onClick={onClose} className="p-3 hover:bg-brand-light rounded-full transition-colors group">
              <SafeIcon icon={FiX} className="w-6 h-6 group-hover:rotate-90 transition-transform" />
            </button>
          </div>

          {/* Error Banner */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 px-5 py-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3"
            >
              <SafeIcon icon={FiAlertCircle} className="text-red-500 w-5 h-5 mt-0.5 shrink-0" />
              <p className="text-red-700 text-sm font-medium">{error}</p>
            </motion.div>
          )}

          {/* ── Step 1: Basic Info ────────────────────────────────────── */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy ml-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  autoFocus
                  className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none transition-all font-medium text-accent-navy placeholder:text-text-muted/40"
                  placeholder="e.g. Eleanor Grace Miller"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy ml-1">Sunrise (Birth)</label>
                  <input
                    type="date"
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy ml-1">Sunset (Passing)</label>
                  <input
                    type="date"
                    name="deathDate"
                    value={formData.deathDate}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy ml-1">Place of Birth</label>
                  <input
                    type="text"
                    name="birthPlace"
                    value={formData.birthPlace}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none font-medium text-accent-navy placeholder:text-text-muted/40"
                    placeholder="e.g. Lagos, Nigeria"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy ml-1">Resting Place</label>
                  <input
                    type="text"
                    name="restingPlace"
                    value={formData.restingPlace}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none font-medium text-accent-navy placeholder:text-text-muted/40"
                    placeholder="e.g. Ikoyi Cemetery"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* ── Step 2: Biography ─────────────────────────────────────── */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy ml-1">Their Journey in Words</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl h-48 resize-none focus:border-accent-gold outline-none transition-all placeholder:text-text-muted/40"
                  placeholder="Share the essence of their life story..."
                />
              </div>
              <div className="p-4 bg-brand-light/50 border border-brand-dark rounded-2xl flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-accent-gold/10 flex items-center justify-center text-accent-gold">
                  <SafeIcon icon={FiZap} className="w-4 h-4" />
                </div>
                <p className="text-[10px] font-bold text-accent-navy uppercase tracking-wider">
                  Biography is optional — you can add or edit it later
                </p>
              </div>
            </motion.div>
          )}

          {/* ── Step 3: Review & Publish ──────────────────────────────── */}
          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
                <div className="p-6 bg-brand-light rounded-[32px] border-2 border-accent-gold relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10">
                    <SafeIcon icon={FiShield} className="w-12 h-12" />
                  </div>
                  <div className="relative z-10">
                    <h4 className="font-serif text-xl text-accent-navy mb-1">Legacy Sanctuary</h4>
                    <p className="text-xs text-text-muted font-light mb-4">Lifetime hosting, advanced privacy, and legacy storage.</p>
                    <div className="flex items-center gap-2 text-accent-gold">
                      <SafeIcon icon={FiCheck} />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em]">Free for All Users</span>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="p-6 bg-brand-light rounded-2xl border border-brand-dark space-y-2">
                  <p className="text-xs font-bold text-accent-navy mb-3">📋 Memorial Summary</p>
                  <div className="space-y-1.5 text-sm">
                    <p className="text-text-muted"><span className="font-bold text-accent-navy">Name:</span> {formData.name || '—'}</p>
                    {formData.birthDate && <p className="text-text-muted"><span className="font-bold text-accent-navy">Born:</span> {new Date(formData.birthDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>}
                    {formData.deathDate && <p className="text-text-muted"><span className="font-bold text-accent-navy">Passed:</span> {new Date(formData.deathDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>}
                    {formData.birthPlace && <p className="text-text-muted"><span className="font-bold text-accent-navy">From:</span> {formData.birthPlace}</p>}
                    {formData.restingPlace && <p className="text-text-muted"><span className="font-bold text-accent-navy">Resting:</span> {formData.restingPlace}</p>}
                    {formData.bio && <p className="text-text-muted mt-2 line-clamp-2 italic">"{formData.bio}"</p>}
                  </div>
                </div>

                <div className="p-6 bg-accent-navy text-white rounded-[32px] flex items-center gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center text-accent-gold shrink-0">
                    <SafeIcon icon={FiShield} className="w-7 h-7" />
                  </div>
                  <div>
                    <h5 className="font-bold text-base mb-1">Privacy & Security</h5>
                    <p className="text-xs opacity-60 font-light">
                      Your memorial will be publicly visible so friends and family can find and pay their respects.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Footer */}
        <div className="p-8 border-t border-brand-dark bg-white flex justify-between items-center flex-shrink-0">
          <span className="text-[10px] font-black uppercase tracking-widest text-text-muted hidden sm:block">
            Step {step} of 3
          </span>
          <div className="flex items-center gap-4 w-full sm:w-auto">
            {step > 1 && (
              <button
                onClick={() => { setStep(s => s - 1); setError(''); }}
                disabled={saving}
                className="flex-1 sm:flex-none px-8 py-4 text-xs font-black uppercase tracking-widest border-2 border-brand-dark rounded-full hover:bg-brand-light transition-all disabled:opacity-50"
              >
                Back
              </button>
            )}
            <button
              onClick={() => {
                if (step < 3) { setError(''); setStep(s => s + 1); }
                else handlePublish();
              }}
              disabled={saving || (step === 1 && !canProceed)}
              className="flex-1 sm:flex-none bg-accent-navy text-white px-10 py-4 text-xs font-black uppercase tracking-widest rounded-full hover:bg-accent-gold transition-all shadow-xl disabled:opacity-60 flex items-center justify-center gap-2 min-w-[160px]"
            >
              {saving ? (
                <>
                  <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                  Publishing...
                </>
              ) : step === 3 ? (
                <><SafeIcon icon={FiCheck} /> Publish Legacy</>
              ) : 'Continue'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CreateMemorialModal;